import React from "react";
import { Tag, SectionHeader, Cell, ScoreBar } from "./Primitives";

// Defensive coercion: LLMs sometimes return objects where we expect strings.
// e.g. {field:"id", type:"uuid"} instead of "id (uuid)". Render gracefully.
const toStr = (x) => {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x === "number" || typeof x === "boolean") return String(x);
  if (Array.isArray(x)) return x.map(toStr).filter(Boolean).join(" · ");
  if (typeof x === "object") {
    // Common shapes: {field, type}, {name, value}, {label, value}, etc.
    if (x.field && x.type) return `${x.field} (${x.type})`;
    if (x.name && x.detail) return `${x.name} — ${x.detail}`;
    if (x.label && x.value) return `${x.label}: ${x.value}`;
    // Generic fallback: key: value pairs
    return Object.entries(x)
      .map(([k, v]) => `${k}: ${toStr(v)}`)
      .join(" · ");
  }
  return String(x);
};

export const SECTIONS = [
  { id: "validation", num: "01", label: "Validation" },
  { id: "revenue", num: "02", label: "Revenue Model" },
  { id: "mvp", num: "03", label: "MVP Design" },
  { id: "ai", num: "04", label: "AI Features" },
  { id: "tech", num: "05", label: "Tech Stack" },
  { id: "db", num: "06", label: "Database" },
  { id: "gtm", num: "07", label: "Strategy" },
  { id: "fin", num: "08", label: "Financials" },
  { id: "risks", num: "09", label: "Risks" },
  { id: "verdict", num: "10", label: "Verdict" },
];

const BlueprintView = ({ data }) => {
  if (!data) return null;
  const v = data.validation || {};
  const rev = data.revenue_model || {};
  const mvp = data.mvp || {};
  const tech = data.tech_stack || {};
  const dbd = data.database_design || {};
  const biz = data.business_strategy || {};
  const fin = data.financials || {};
  const verdict = data.verdict || {};

  return (
    <article data-testid="blueprint-view" className="space-y-28 sm:space-y-36">
      {/* Header / Memo cover */}
      <header className="border border-[#0A0A0A]/15 bg-[#F9F8F6] p-8 sm:p-12">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Tag intent="klein">BluePrintAI · MEMO</Tag>
          {data.industry && <Tag>{data.industry}</Tag>}
          {data.target_market && <Tag>{data.target_market}</Tag>}
          <Tag>GENERATED · {data.generated_at}</Tag>
        </div>
        <h1 className="font-serif font-light text-5xl sm:text-7xl tracking-tighter leading-[0.95] text-[#0A0A0A] max-w-5xl">
          {data.title}
        </h1>
        {data.tagline && (
          <p className="mt-6 text-lg sm:text-xl text-[#0A0A0A]/70 max-w-3xl leading-relaxed">
            {data.tagline}
          </p>
        )}
        {data.idea && (
          <div className="mt-8 pt-6 border-t border-[#0A0A0A]/12">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-2">
              The Idea
            </div>
            <p className="font-sans text-sm sm:text-base text-[#0A0A0A]/75 max-w-4xl leading-relaxed">
              {data.idea}
            </p>
          </div>
        )}
      </header>

      {/* 01 VALIDATION */}
      <section id="validation" data-testid="section-validation">
        <SectionHeader number="01" eyebrow="Validation" title="Is this worth building in 2026?" kicker={v.verdict} />
        {v.thesis && (
          <blockquote className="border-l-2 border-[#002FA7] pl-6 py-2 mb-10 max-w-3xl">
            <p className="font-serif text-xl sm:text-2xl italic leading-snug text-[#0A0A0A]">"{v.thesis}"</p>
          </blockquote>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 -mb-px">
          {(v.market_size || []).map((m, i) => (
            <Cell key={i} label={m.label} value={m.value} note={m.note} accent={i === 0} testid={`market-${m.label}`} />
          ))}
        </div>

        {v.problem_severity && (
          <div className="mt-12">
            <div className="flex items-baseline gap-4 mb-4">
              <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C]">Problem Severity</span>
              <span className="font-serif text-2xl text-[#002FA7]">{v.problem_severity.score}</span>
            </div>
            <ul className="space-y-3 max-w-3xl">
              {(v.problem_severity.drivers || []).map((d, i) => (
                <li key={i} className="flex gap-4 text-sm leading-relaxed text-[#0A0A0A]/80">
                  <span className="font-mono text-[10px] text-[#002FA7] mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <span>{toStr(d)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Competitor table */}
        {v.competitors && v.competitors.length > 0 && (
          <div className="mt-14">
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-4">Competitive Landscape</div>
            <div className="border border-[#0A0A0A]/15">
              <div className="grid grid-cols-12 bg-[#0A0A0A] text-[#F9F8F6] font-mono text-[10px] tracking-[0.18em] uppercase">
                <div className="col-span-3 p-3 border-r border-[#F9F8F6]/15">Competitor</div>
                <div className="col-span-4 p-3 border-r border-[#F9F8F6]/15">Strength</div>
                <div className="col-span-5 p-3">Weakness / Opening</div>
              </div>
              {v.competitors.map((c, i) => (
                <div key={i} className="grid grid-cols-12 text-sm border-t border-[#0A0A0A]/15">
                  <div className="col-span-3 p-4 font-serif text-base border-r border-[#0A0A0A]/12">{c.name}</div>
                  <div className="col-span-4 p-4 text-[#0A0A0A]/75 border-r border-[#0A0A0A]/12">{c.strength}</div>
                  <div className="col-span-5 p-4 text-[#0A0A0A]/75">{c.weakness}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {v.market_gaps && (
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-4">Market Gaps</div>
              <ul className="space-y-3">
                {v.market_gaps.map((g, i) => (
                  <li key={i} className="text-sm leading-relaxed text-[#0A0A0A]/80 pl-4 border-l border-[#002FA7]/40">
                    {toStr(g)}
                  </li>
                ))}
              </ul>
            </div>
            {v.ai_advantage && (
              <div>
                <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-4">AI vs Traditional Marketplaces</div>
                <p className="font-serif text-lg leading-relaxed text-[#0A0A0A]/85 italic">{v.ai_advantage}</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* 02 REVENUE */}
      <section id="revenue" data-testid="section-revenue">
        <SectionHeader number="02" eyebrow="Monetization" title="How this prints money." />

        {rev.streams && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 -mb-px">
            {rev.streams.map((s, i) => (
              <div key={i} className="border border-[#0A0A0A]/15 p-6 bg-[#F9F8F6] -ml-px -mt-px">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#002FA7] mb-3">
                  Stream · {String(i + 1).padStart(2, "0")}
                </div>
                <h4 className="font-serif text-xl mb-3 leading-tight">{s.name}</h4>
                <p className="text-sm text-[#0A0A0A]/70 leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        )}

        {rev.projections && (
          <div className="mt-14">
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-4">
              Revenue Projections
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {rev.projections.map((p, i) => (
                <div
                  key={i}
                  className={`border border-[#0A0A0A]/15 p-7 -ml-px -mt-px ${i === 1 ? "bg-[#0A0A0A] text-[#F9F8F6]" : "bg-[#F9F8F6]"}`}
                >
                  <div className={`font-mono text-[10px] tracking-[0.22em] uppercase mb-4 ${i === 1 ? "text-[#F9F8F6]/60" : "text-[#5C5C5C]"}`}>
                    {p.year}
                  </div>
                  {p.gmv && p.gmv !== "N/A" && (
                    <div className="mb-3">
                      <div className={`text-[10px] font-mono uppercase tracking-widest ${i === 1 ? "text-[#F9F8F6]/50" : "text-[#5C5C5C]"}`}>GMV</div>
                      <div className="font-serif text-2xl font-light">{p.gmv}</div>
                    </div>
                  )}
                  <div className="mb-3">
                    <div className={`text-[10px] font-mono uppercase tracking-widest ${i === 1 ? "text-[#F9F8F6]/50" : "text-[#5C5C5C]"}`}>Net Revenue</div>
                    <div className="font-serif text-3xl font-light">{p.net_revenue}</div>
                  </div>
                  <p className={`text-xs leading-relaxed mt-3 ${i === 1 ? "text-[#F9F8F6]/70" : "text-[#0A0A0A]/65"}`}>
                    {p.assumptions}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 03 MVP */}
      <section id="mvp" data-testid="section-mvp">
        <SectionHeader number="03" eyebrow="MVP" title="The cheapest, scalable first version." kicker={mvp.philosophy} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 -mb-px">
          <div className="border border-[#0A0A0A]/15 p-7 bg-[#F9F8F6]">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-4">Build · Core Features</div>
            <ul className="space-y-3">
              {(mvp.core_features || []).map((f, i) => (
                <li key={i} className="text-sm leading-relaxed text-[#0A0A0A]/85 flex gap-3">
                  <span className="text-[#002FA7] font-mono text-xs">+</span>
                  <span>{toStr(f)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[#0A0A0A]/15 p-7 bg-[#0A0A0A] text-[#F9F8F6] -ml-px lg:-ml-px lg:-mt-0 -mt-px">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#F9F8F6]/60 mb-4">Do NOT Build</div>
            <ul className="space-y-3">
              {(mvp.do_not_build || []).map((f, i) => (
                <li key={i} className="text-sm leading-relaxed text-[#F9F8F6]/85 flex gap-3">
                  <span className="text-[#F9F8F6]/50 font-mono text-xs">×</span>
                  <span>{toStr(f)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 -mb-px mt-0">
          <div className="border border-[#0A0A0A]/15 p-6 bg-[#F9F8F6] -mt-px">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-2">Approach</div>
            <p className="text-sm text-[#0A0A0A]/80 leading-relaxed">{toStr(mvp.approach)}</p>
          </div>
          <div className="border border-[#0A0A0A]/15 p-6 bg-[#F9F8F6] -ml-px -mt-px">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-2">Timeline</div>
            <p className="text-sm text-[#0A0A0A]/80 leading-relaxed">{toStr(mvp.timeline)}</p>
          </div>
        </div>

        {mvp.cost_india && (
          <div className="mt-10">
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-4">Cost Estimate · India</div>
            <div className="border border-[#0A0A0A]/15">
              {mvp.cost_india.map((c, i) => (
                <div key={i} className={`grid grid-cols-12 text-sm border-t border-[#0A0A0A]/12 first:border-t-0 ${i === mvp.cost_india.length - 1 ? "bg-[#0A0A0A] text-[#F9F8F6]" : ""}`}>
                  <div className="col-span-8 p-4 font-sans">{c.item}</div>
                  <div className="col-span-4 p-4 font-mono text-right border-l border-[#0A0A0A]/12">{c.amount}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 04 AI FEATURES */}
      <section id="ai" data-testid="section-ai">
        <SectionHeader number="04" eyebrow="AI" title="Where AI does real work." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 -mb-px">
          {(data.ai_features || []).map((a, i) => (
            <div key={i} className="border border-[#0A0A0A]/15 p-6 bg-[#F9F8F6] -ml-px -mt-px">
              <div className="flex items-baseline justify-between mb-3">
                <h4 className="font-serif text-xl">{a.name}</h4>
                <span className="font-mono text-[10px] tracking-[0.2em] text-[#002FA7]">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <p className="text-sm text-[#0A0A0A]/75 leading-relaxed mb-4">{toStr(a.how)}</p>
              {a.model && (
                <div className="border-t border-[#0A0A0A]/12 pt-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C5C5C] mb-1">Model</div>
                  <div className="font-mono text-xs text-[#002FA7]">{toStr(a.model)}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 05 TECH STACK */}
      <section id="tech" data-testid="section-tech">
        <SectionHeader number="05" eyebrow="Architecture" title="Production-grade tech stack." />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 -mb-px">
          {tech.frontend && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Frontend</div>
              <div className="mb-3"><span className="font-mono text-[10px] uppercase text-[#5C5C5C] mr-2">Web</span><span className="text-sm">{toStr(tech.frontend.web)}</span></div>
              <div><span className="font-mono text-[10px] uppercase text-[#5C5C5C] mr-2">Mobile</span><span className="text-sm">{toStr(tech.frontend.mobile)}</span></div>
            </div>
          )}
          {tech.backend && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Backend</div>
              <div className="mb-3"><span className="font-mono text-[10px] uppercase text-[#5C5C5C] mr-2">Framework</span><span className="text-sm">{tech.backend.framework}</span></div>
              <div><span className="font-mono text-[10px] uppercase text-[#5C5C5C] mr-2">Arch</span><span className="text-sm">{tech.backend.architecture}</span></div>
            </div>
          )}
          {tech.database && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px lg:col-span-2">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Database Layer</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                {Object.entries(tech.database).map(([k, val]) => (
                  <div key={k}>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C5C5C] mb-1">{k}</div>
                    <div>{toStr(val)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tech.cloud && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px lg:col-span-2 bg-[#0A0A0A] text-[#F9F8F6]">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#F9F8F6]/60 mb-3">Cloud Architecture</div>
              <p className="text-sm text-[#F9F8F6]/85">{toStr(tech.cloud)}</p>
            </div>
          )}
          {tech.devops && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">DevOps</div>
              <ul className="space-y-2 text-sm text-[#0A0A0A]/80">{tech.devops.map((x, i) => <li key={i}>· {toStr(x)}</li>)}</ul>
            </div>
          )}
          {tech.security && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Security</div>
              <ul className="space-y-2 text-sm text-[#0A0A0A]/80">{tech.security.map((x, i) => <li key={i}>· {toStr(x)}</li>)}</ul>
            </div>
          )}
          {tech.cost_optimization && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px lg:col-span-2">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Cost Optimization</div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#0A0A0A]/80">{tech.cost_optimization.map((x, i) => <li key={i}>· {toStr(x)}</li>)}</ul>
            </div>
          )}
        </div>
      </section>

      {/* 06 DATABASE */}
      <section id="db" data-testid="section-db">
        <SectionHeader number="06" eyebrow="Schema" title="Production database design." kicker={dbd.philosophy} />
        {dbd.entities && (
          <div className="border border-[#0A0A0A]/15">
            {dbd.entities.map((e, i) => (
              <div key={i} className="border-t border-[#0A0A0A]/12 first:border-t-0 grid grid-cols-12">
                <div className="col-span-12 md:col-span-3 p-4 border-r border-[#0A0A0A]/12 bg-[#EFEFE8]">
                  <div className="font-mono text-[11px] tracking-widest text-[#002FA7] uppercase">{e.name}</div>
                </div>
                <div className="col-span-12 md:col-span-9 p-4">
                  <div className="flex flex-wrap gap-2">
                    {e.fields.map((f, j) => (
                      <span key={j} className="font-mono text-[11px] bg-[#F9F8F6] border border-[#0A0A0A]/10 px-2 py-1">{f}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {dbd.indexing && (
          <div className="mt-8">
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-3">Indexing Strategy</div>
            <ul className="space-y-2 text-sm text-[#0A0A0A]/80 max-w-4xl">{dbd.indexing.map((x, i) => <li key={i} className="border-l border-[#002FA7]/40 pl-4">{toStr(x)}</li>)}</ul>
          </div>
        )}
      </section>

      {/* 07 GTM */}
      <section id="gtm" data-testid="section-gtm">
        <SectionHeader number="07" eyebrow="Go-to-Market" title="From zero to a thousand users." kicker={biz.gtm} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 -mb-px">
          {biz.first_1000_users && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">First 1,000 Users</div>
              <ul className="space-y-3 text-sm text-[#0A0A0A]/80">{biz.first_1000_users.map((x, i) => <li key={i}>· {toStr(x)}</li>)}</ul>
            </div>
          )}
          {biz.city_launch_playbook && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">City Launch Playbook</div>
              <ol className="space-y-3 text-sm text-[#0A0A0A]/80 list-decimal list-inside">{biz.city_launch_playbook.map((x, i) => <li key={i}>{toStr(x)}</li>)}</ol>
            </div>
          )}
          {biz.acquisition_channels && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Acquisition Channels</div>
              <div className="flex flex-wrap gap-2">{biz.acquisition_channels.map((x, i) => <Tag key={i}>{toStr(x)}</Tag>)}</div>
            </div>
          )}
          {biz.partnerships && (
            <div className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-3">Partnerships</div>
              <ul className="space-y-2 text-sm text-[#0A0A0A]/80">{biz.partnerships.map((x, i) => <li key={i}>· {toStr(x)}</li>)}</ul>
            </div>
          )}
        </div>
        {biz.seo_strategy && (
          <div className="mt-8 border-l-2 border-[#002FA7] pl-6 py-2 max-w-3xl">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-2">SEO Strategy</div>
            <p className="font-serif text-lg italic text-[#0A0A0A]/85 leading-relaxed">{toStr(biz.seo_strategy)}</p>
          </div>
        )}
      </section>

      {/* 08 FINANCIALS */}
      <section id="fin" data-testid="section-fin">
        <SectionHeader number="08" eyebrow="Financials" title="The cost of building this thing." />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 -mb-px">
          {fin.startup_cost && (
            <div
              data-testid="fin-startup"
              className="border border-[#0A0A0A]/15 p-7 sm:p-8 -ml-px -mt-px bg-[#0A0A0A] text-[#F9F8F6]"
            >
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#F9F8F6]/60">
                  Startup Cost
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] text-[#F9F8F6]/40">01</span>
              </div>
              <p className="font-serif text-2xl leading-snug tracking-tight text-[#F9F8F6] font-light">
                {toStr(fin.startup_cost)}
              </p>
            </div>
          )}
          {fin.burn_rate && (
            <div
              data-testid="fin-burn"
              className="border border-[#0A0A0A]/15 p-7 sm:p-8 -ml-px -mt-px bg-[#F9F8F6]"
            >
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">
                  Burn Rate
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] text-[#5C5C5C]">02</span>
              </div>
              <p className="font-sans text-sm sm:text-base text-[#0A0A0A]/85 leading-relaxed">
                {toStr(fin.burn_rate)}
              </p>
            </div>
          )}
          {fin.break_even && (
            <div
              data-testid="fin-be"
              className="border border-[#0A0A0A]/15 p-7 sm:p-8 -ml-px -mt-px bg-[#F9F8F6]"
            >
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">
                  Break-Even
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] text-[#5C5C5C]">03</span>
              </div>
              <p className="font-sans text-sm sm:text-base text-[#0A0A0A]/85 leading-relaxed">
                {toStr(fin.break_even)}
              </p>
            </div>
          )}
          {fin.profitability && (
            <div
              data-testid="fin-prof"
              className="border border-[#0A0A0A]/15 p-7 sm:p-8 -ml-px -mt-px bg-[#EFEFE8]"
            >
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7]">
                  Profitability
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] text-[#5C5C5C]">04</span>
              </div>
              <p className="font-sans text-sm sm:text-base text-[#0A0A0A]/85 leading-relaxed">
                {toStr(fin.profitability)}
              </p>
            </div>
          )}
        </div>

        {fin.team_y1 && (
          <div className="mt-12">
            <div className="flex items-baseline justify-between mb-4">
              <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C]">
                Team · Year 1
              </span>
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
                {fin.team_y1.reduce((s, t) => s + (t.count || 0), 0)} hires · {fin.team_y1.length} roles
              </span>
            </div>
            <div className="border border-[#0A0A0A]/15">
              {fin.team_y1.map((t, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 text-sm border-t border-[#0A0A0A]/12 first:border-t-0 hover:bg-[#EFEFE8]/40"
                >
                  <div className="col-span-2 sm:col-span-1 p-4 font-mono text-[#002FA7] text-center border-r border-[#0A0A0A]/12 self-center">
                    {t.count}×
                  </div>
                  <div className="col-span-10 sm:col-span-11 p-4 font-serif text-base sm:text-lg leading-tight">
                    {toStr(t.role)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 09 RISKS */}
      <section id="risks" data-testid="section-risks">
        <SectionHeader number="09" eyebrow="Risks" title="What could kill this." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 -mb-px">
          {(data.risks || []).map((r, i) => (
            <div key={i} className="border border-[#0A0A0A]/15 p-6 -ml-px -mt-px bg-[#F9F8F6]">
              <div className="flex items-center gap-3 mb-3">
                <Tag intent={r.severity === "HIGH" ? "high" : r.severity === "MEDIUM" ? "medium" : "low"}>
                  {toStr(r.severity)}
                </Tag>
                <span className="font-mono text-[10px] text-[#5C5C5C]">RISK · {String(i + 1).padStart(2, "0")}</span>
              </div>
              <h4 className="font-serif text-xl mb-3 leading-tight">{toStr(r.risk)}</h4>
              <p className="text-sm text-[#0A0A0A]/75 leading-relaxed">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#002FA7] mr-2">Mitigation</span>
                {toStr(r.mitigation)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 10 VERDICT */}
      <section id="verdict" data-testid="section-verdict">
        <SectionHeader number="10" eyebrow="Verdict" title="The honest call." />

        <div className="border border-[#0A0A0A]/15 bg-[#0A0A0A] text-[#F9F8F6] p-8 sm:p-12 mb-10">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#F9F8F6]/60 mb-3">Overall Score</div>
          <div className="font-serif text-7xl sm:text-8xl font-light tracking-tighter text-[#F9F8F6]">{toStr(verdict.overall_score)}</div>
        </div>

        {verdict.ratings && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 mb-12 max-w-4xl">
            {verdict.ratings.map((r, i) => (
              <div key={i}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-serif text-lg">{toStr(r.dimension)}</span>
                  <span className="font-mono text-xs text-[#5C5C5C]">{r.score}/10</span>
                </div>
                <ScoreBar score={r.score} />
                {r.note && <p className="text-xs text-[#0A0A0A]/60 mt-2 leading-relaxed">{toStr(r.note)}</p>}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 -mb-px">
          {verdict.best_niche && <Cell label="Best Niche to Start" value="↘" note={toStr(verdict.best_niche)} testid="best-niche" />}
          {verdict.best_launch_city && <Cell label="Best Launch City" value={toStr(verdict.best_launch_city)} testid="best-city" />}
          {verdict.cheapest_validation && <Cell label="Cheapest Validation" value="₹" note={toStr(verdict.cheapest_validation)} testid="cheapest" />}
        </div>

        {verdict.ninety_day_roadmap && (
          <div className="mt-14">
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C] mb-4">90-Day Execution Roadmap</div>
            <ol className="space-y-0 border border-[#0A0A0A]/15">
              {verdict.ninety_day_roadmap.map((p, i) => (
                <li key={i} className="grid grid-cols-12 border-t border-[#0A0A0A]/12 first:border-t-0">
                  <div className="col-span-12 md:col-span-4 p-5 bg-[#EFEFE8] border-r border-[#0A0A0A]/12">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-1">Phase {String(i + 1).padStart(2, "0")}</div>
                    <div className="font-serif text-lg leading-tight">{toStr(p.phase)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-8 p-5">
                    <ul className="space-y-2 text-sm text-[#0A0A0A]/80">
                      {(p.actions || []).map((a, j) => (
                        <li key={j} className="flex gap-3">
                          <span className="text-[#002FA7] font-mono text-xs">→</span>
                          <span>{toStr(a)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>
    </article>
  );
};

export default BlueprintView;
